/**
 * DO NOT EDIT — GENERATED FILE.
 *
 * Generated from backend/src/types/wire.ts by
 * `cd backend && npm run generate:wire-types`. Edit the backend source and
 * re-run the generator; CI fails when this copy drifts from it.
 */

// ---------------------------------------------------------------------------
// AI providers and models
// ---------------------------------------------------------------------------

export const AI_PROVIDERS = ['google', 'openai', 'anthropic', 'selfhosted'] as const;
export type AIProvider = typeof AI_PROVIDERS[number];

export const GOOGLE_MODELS = [
    'gemini-2.5-flash-lite',
    'gemini-2.0-flash-exp',
    'gemini-1.5-flash',
    'gemini-1.5-pro',
    'gemini-1.0-pro',
] as const;
export type GoogleModel = typeof GOOGLE_MODELS[number];

export const OPENAI_MODELS = [
    'gpt-4o-mini',
    'gpt-4o',
    'gpt-4-turbo',
    'gpt-3.5-turbo',
] as const;
export type OpenAIModel = typeof OPENAI_MODELS[number];

export const ANTHROPIC_MODELS = [
    'claude-opus-5',
    'claude-sonnet-5',
    'claude-haiku-4-5',
] as const;
export type AnthropicModel = typeof ANTHROPIC_MODELS[number];

export const ALL_MODELS = [
    ...GOOGLE_MODELS,
    ...OPENAI_MODELS,
    ...ANTHROPIC_MODELS,
] as const;
export type AIModel = typeof ALL_MODELS[number];

export const MODEL_PROVIDER_MAP: Record<string, AIProvider> = {
    // Google models
    'gemini-2.5-flash-lite': 'google',
    'gemini-2.0-flash-exp': 'google',
    'gemini-1.5-flash': 'google',
    'gemini-1.5-pro': 'google',
    'gemini-1.0-pro': 'google',
    // OpenAI models
    'gpt-4o-mini': 'openai',
    'gpt-4o': 'openai',
    'gpt-4-turbo': 'openai',
    'gpt-3.5-turbo': 'openai',
    // Anthropic models
    'claude-opus-5': 'anthropic',
    'claude-sonnet-5': 'anthropic',
    'claude-haiku-4-5': 'anthropic',
};

export const DEFAULT_PROVIDER: AIProvider = 'openai';
export const DEFAULT_MODEL = 'gpt-4o-mini';
export const DEFAULT_GOOGLE_MODEL: GoogleModel = 'gemini-2.5-flash-lite';
export const DEFAULT_OPENAI_MODEL: OpenAIModel = 'gpt-4o-mini';
export const DEFAULT_ANTHROPIC_MODEL: AnthropicModel = 'claude-haiku-4-5';

/** Provider availability as reported by GET /api/analysis/providers. */
export interface AIProviderInfo {
    provider: AIProvider;
    models: readonly string[];
    available: boolean;
}

/** The provider a model belongs to, or undefined for an unknown model. */
export function getProviderForModel(model: string): AIProvider | undefined {
    return MODEL_PROVIDER_MAP[model];
}

/** True when the model is one of the supported models. */
export function isValidModel(model: string): boolean {
    return model in MODEL_PROVIDER_MAP;
}

// ---------------------------------------------------------------------------
// Prompts (blueprints)
// ---------------------------------------------------------------------------

export interface TargetCategories {
    primary: number[];
    secondary: number[];
}

/**
 * A prompt as sent over the wire. The backend-internal variant adds the
 * embedding columns and uses `Date` timestamps — see `types/index.ts`.
 */
export interface BrandSafetyPrompt {
    id: string;
    name: string;
    description: string;
    prompt: string;
    type: 'brand-safety' | 'audience-targeting';
    userId?: string; // null for system/default prompts, user_id for user-owned prompts
    companyId?: string; // null for default prompts, company_id for company-owned prompts
    countries?: string; // Comma-separated lowercase ISO2 country codes (e.g., "lt,es,us")
    targetCategories?: TargetCategories;
    excludedCategories?: number[];
    createdAt: string;
    updatedAt: string;
}

export interface PromptImprovement {
    originalPrompt: string;
    improvedPrompt: string;
    reasoning: string;
    feedbackIncorporated: string;
}

// ---------------------------------------------------------------------------
// Analysis
// ---------------------------------------------------------------------------

export interface AnalysisRequest {
    urls: string[];
    promptId: string;
    provider?: AIProvider;
    model?: string;
    keywordListId?: string;
    analyzeFromDatabase?: boolean;
    databaseUrlCount?: number;
    /** Legacy single-country selector (kept for older clients / direct API callers). */
    countryTld?: string;
    /** Multi-country selector for the Relevance Engine default (DB) mode. */
    countryCodes?: string[];
    analyzeFromClassifications?: boolean;
    classificationUrlCount?: number;
    classificationDomains?: string[];
    classificationCountry?: string | string[];
    classificationSimilaritySelectivity?: number;
}

export interface KeywordMatch {
    keyword: string;
    count: number;
    listName: string;
}

export interface KeywordBlockingResult {
    isBlocked: boolean;
    matchedKeywords: string[];
    /** Per-keyword breakdown; only populated by the keyword-list scan path. */
    keywordMatches?: KeywordMatch[];
    totalMatches: number;
    reasoning: string;
}

/**
 * One analysed URL as sent over the wire. The backend-internal variant uses a
 * `Date` for `analyzedAt` — see `types/index.ts`.
 */
export interface AnalysisResult {
    id: string;
    url: string;
    urlHash?: string; // Optional url_hash from rtb_stats
    promptId: string;
    userId?: string;
    companyId?: string;
    bulkJobId?: string;
    isBrandSafe: boolean;
    reasoning: string;
    confidence: number;
    contentSummary: string;
    dailyRequests?: number;
    /** null means the value was never computed — distinct from a genuine zero. */
    dailyAvails?: number | null;
    estimatedCost?: number;
    actualCost?: number;
    currentTotalCost?: number;
    analyzedAt: string;
    provider?: AIProvider;
    model?: string;
    keywordBlocking?: KeywordBlockingResult;
    inputTokens?: number;
    outputTokens?: number;
    feedback?: {
        agree: boolean;
        reasoning?: string;
        timestamp: string;
    };
}

export interface BulkAnalysisResult {
    id: string;
    promptId: string;
    userId?: string;
    companyId?: string;
    results: AnalysisResult[];
    totalCost: number;
    analyzedAt: string;
    countryCodes?: string[];
    summary: {
        totalUrls: number;
        matchedUrls: number;
        unmatchedUrls: number;
        failedUrls?: number;
        averageConfidence: number;
        totalInputTokens?: number;
        totalOutputTokens?: number;
        totalRequestsMatched?: number;
        totalRequestsUnmatched?: number;
    };
}

// ---------------------------------------------------------------------------
// Analysis history — per-market result counts (deepcontext-z5mx.1)
// ---------------------------------------------------------------------------

/** A resolved market bucket for a blueprint's scan results. */
export interface AnalysisMarket {
    /** ISO country code (uppercase), 'ALL' (job ran unrestricted), or
     * 'UNRECORDED' (no country recorded anywhere: neither a scheduled_job_runs
     * row nor bulk_scan_jobs.country_codes — e.g. a manual scan that predates
     * migration 024, 2026-09-04). */
    market: string;
    label: string;
    resultCount: number;
}

export interface AnalysisMarketsResponse {
    success: true;
    markets: AnalysisMarket[];
}

export interface CostEstimate {
    inputTokens: number;
    outputTokens: number;
    estimatedCost: number;
    breakdown: {
        inputCost: number;
        outputCost: number;
    };
}

export interface FeedbackRequest {
    analysisId: string;
    agree: boolean;
    reasoning?: string;
}

// ---------------------------------------------------------------------------
// Pagination
//
// Two distinct envelopes used to share the name `PaginatedResponse` on the two
// sides. They are NOT interchangeable, so each has its own name now.
// ---------------------------------------------------------------------------

/** Offset pagination: prompts, site lists, domains. */
export interface OffsetPaginatedResponse<T> {
    data: T[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
}

export interface PaginationInfo {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    nextPage: number | null;
    previousPage: number | null;
}

/** Enveloped pagination: the analysis-history endpoint. */
export interface EnvelopedPaginatedResponse<T> {
    success: boolean;
    results: T[];
    pagination: PaginationInfo;
    counts?: {
        safe: number;
        unsafe: number;
        total: number;
    };
    totalDailyRequests?: number;
    dataSource?: 'mysql' | 'clickhouse';
}

// ---------------------------------------------------------------------------
// Scheduled-job performance (GET /api/scheduled-jobs/performance)
// ---------------------------------------------------------------------------

/**
 * Percentage metrics shared by the daily rows, the per-job breakdown, and the
 * window totals of the scheduled-job performance endpoint. All three
 * percentages are guarded against zero denominators (0, never NaN/null).
 */
export interface ScheduledJobPerformanceMetrics {
    runs: number;
    completedRuns: number;
    urlsProcessed: number;
    // SUM(totalUrls) / SUM(urls_per_run of the run's job) * 100
    fetchFulfillmentPct: number;
    // SUM(matchedUrls) / (SUM(matchedUrls) + SUM(unmatchedUrls)) * 100
    matchRatePct: number;
    // SUM(droppedUrls) / SUM(totalUrls) * 100
    dropRatePct: number;
    // Raw matched-URL count, pooled (summed) like matchedBidRequests — NOT
    // averaged per blueprint like the percentages above (deepcontext-0dl7).
    // The volume signal behind matchRatePct and the direct driver of
    // matchedBidRequests.
    matchedUrls: number;
    // SUM(sites_url_data.bid_request_count) for matched URLs, per the run's own
    // date. null when no run in scope has a stored value (deepcontext-ge91.1).
    matchedBidRequests: number | null;
}

export interface ScheduledJobPerformanceDay extends ScheduledJobPerformanceMetrics {
    date: string;
    // Count of distinct jobs with >=1 run that day (deepcontext-b292) — idle
    // jobs (configured but no run) don't count. Lets the frontend show a
    // thin active-jobs-per-day indicator alongside the volume bars, so a
    // count drop (e.g. campaign expiry) reads as fewer jobs rather than a
    // deployment regression.
    activeJobs: number;
}

export interface ScheduledJobPerformanceByJob extends ScheduledJobPerformanceMetrics {
    jobId: string;
    jobName: string;
}

/**
 * One day of the outcome series: pooled spend/win-rate across every DISTINCT
 * campaign linked to any in-scope blueprint (deepcontext-izaw.2's
 * `getJobPerformanceDspData`, a campaign shared by two blueprints counts
 * once). `winRatePct` is impressions / bids * 100 on that day's pooled
 * totals, 0 when bids is 0 — same pct convention as fetchFulfillmentPct etc.
 * above.
 */
export interface ScheduledJobPerformanceOutcomeDay {
    date: string;
    spend: number;
    winRatePct: number;
}

/**
 * DSP-sourced outcome block (deepcontext-izaw.3): daily spend/win-rate series
 * plus window totals, for the Job Performance page's second graph. The DSP
 * read has an 8s hard timeout; on failure/timeout `available` is false and
 * `daily`/`totals` are empty/zeroed rather than failing the whole response —
 * the rest of ScheduledJobPerformance (DeepContext-side metrics) is
 * unaffected.
 */
export interface ScheduledJobPerformanceOutcome {
    available: boolean;
    daily: ScheduledJobPerformanceOutcomeDay[];
    totals: {spend: number; winRatePct: number};
}

/**
 * One blueprint's row in the Job Performance breakdown table
 * (deepcontext-izaw.3). `matchRatePct` (daily_scan_analytics) and
 * `matchedBidRequests` (bulk_scan_jobs.summary, deepcontext-izaw.1) are
 * DeepContext-side and always populated; `winRatePct`/`spend` come from the
 * same DSP read as `ScheduledJobPerformanceOutcome` and are null when that
 * read is unavailable OR the blueprint has no linked campaign.
 *
 * Deliberately NO fetch-fulfillment/drop percentage here: a job fetches its
 * URL pool once and hands the identical pool to every blueprint it holds, so
 * those two figures are properties of the fetch and would just repeat the
 * per-job row down every one of its blueprints.
 */
export interface ScheduledJobPerformanceByBlueprint {
    blueprintId: string;
    matchRatePct: number;
    matchedBidRequests: number | null;
    winRatePct: number | null;
    spend: number | null;
}

/**
 * Response of GET /api/scheduled-jobs/performance. `days` is ordered
 * ascending by date. `totals` is the window aggregate (KPI strip); `byJob` is
 * the same metrics broken down per scheduled_job_id.
 *
 * `perJobDays` is only present when the request opts in with
 * `includePerJobDays=1` (deepcontext-qwjt): each listed job's own daily
 * series, keyed by jobId, computed from the same rows as `byJob` in one pass
 * instead of the caller re-fetching per job. Omitted (not just empty) on the
 * default/single-job request so that existing callers' response shape is
 * unchanged.
 *
 * `outcome` and `byBlueprint` (deepcontext-izaw.3) are scoped to blueprints
 * belonging to a job that actually ran in the window — the same "runs > 0"
 * treatment `byJob` already applies, so an idle job's blueprints don't show
 * up with a hollow zero row.
 */
export interface ScheduledJobPerformance {
    days: ScheduledJobPerformanceDay[];
    totals: ScheduledJobPerformanceMetrics;
    byJob: ScheduledJobPerformanceByJob[];
    perJobDays?: Record<string, ScheduledJobPerformanceDay[]>;
    outcome: ScheduledJobPerformanceOutcome;
    byBlueprint: ScheduledJobPerformanceByBlueprint[];
}

/** One deploy marker as sent by GET /api/scheduled-jobs/release-markers. */
export interface ReleaseMarker {
    version: string;
    deployedAt: string;
}

// ---------------------------------------------------------------------------
// Prompt (blueprint) listing endpoints
// ---------------------------------------------------------------------------

export interface PromptOffsetPaginationInfo {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
}

/** GET /api/prompts (offset pagination). */
export interface PromptPaginatedResponse {
    prompts: BrandSafetyPrompt[];
    pagination: PromptOffsetPaginationInfo;
    cached?: boolean;
}

export interface PromptCursorPaginationInfo {
    nextCursor: string | null;
    prevCursor: string | null;
    hasMore: boolean;
    pageSize: number;
}

/** GET /api/prompts/cursor (keyset pagination). */
export interface PromptCursorResponse {
    prompts: BrandSafetyPrompt[];
    pagination: PromptCursorPaginationInfo;
    cached?: boolean;
}

// ---------------------------------------------------------------------------
// Grader (admin prompt-eval playground, /api/grader/*)
// ---------------------------------------------------------------------------

/** GET /api/grader/current-prompt. */
export interface GraderCurrentPrompt {
    mode: string;
    promptType: string;
    blueprintPlaceholder: string;
    template: string;
}

export interface GraderSampleUrl {
    url: string;
    urlHash: string;
    content: string | null;
    hasContent: boolean;
    scannedAt?: string;
}

/** GET /api/grader/prompts/:promptId/sample. */
export interface GraderSampleResponse {
    mode: string;
    promptId: string;
    requested: number;
    returned: number;
    samples: GraderSampleUrl[];
}

export interface GraderRunSampleInput {
    url: string;
    content?: string | null;
    category_tier?: number;
}

export interface GraderVerdict {
    url: string;
    isBrandSafe: boolean;
    confidence: number;
    reasoning: string;
    contentSummary?: string;
    error?: string;
}

/** POST /api/grader/run. */
export interface GraderRunResponse {
    mode: string;
    provider: string;
    model: string;
    promptId: string;
    rawOutput: string;
    verdicts: GraderVerdict[];
    error?: string;
    usage?: {inputTokens?: number; outputTokens?: number};
}

/** A single candidate verdict to be judged (matches POST /api/grader/run output). */
export interface CandidateVerdict {
    url: string;
    isBrandSafe: boolean;
    confidence: number;
    reasoning: string;
    contentSummary?: string;
    /** The content the candidate saw, used to ground the judge. */
    content?: string | null;
}

/** The judged result for one URL. */
export interface GraderJudgedUrl {
    url: string;
    /** 0-10, clamped. */
    score: number;
    strengths: string[];
    weaknesses: string[];
    reasoning: string;
    /** Set when the judge response for this URL could not be parsed. */
    error?: string;
}

/** POST /api/grader/judge. */
export interface GraderJudgeResponse {
    mode: string;
    provider: string;
    model: string;
    /** Mean of per-URL scores, 0-10, rounded to 2 dp. undefined when no URLs scored. */
    runScore: number | undefined;
    judged: GraderJudgedUrl[];
}

// ---------------------------------------------------------------------------
// Universe / Clusters sidebar (/api/clusters/*) — story-backed since deepcontext-b3to.7
// ---------------------------------------------------------------------------

/** GET /api/clusters?market=<COUNTRY> — story bubble-map payload (deepcontext-b3to.7). */
export interface ClustersPayload {
    market: string;
    generated_at: string;
    /** Top 20 by 24h ad requests, sized for bubbles (area ∝ bid_requests_24h, no floor). */
    top: TrendingStoryPayload[];
    /** Ranked remainder, same ordering as `top`. */
    rest: TrendingStoryPayload[];
    snapshot_hours: number;
    /** Count of orphan (singleton, never-shown) groups for this market. */
    orphan_count: number;
    cached_at: string;
}

export interface ClustersStoryMember {
    url_hash: string;
    url: string;
    title: string;
    site_domain: string;
    join_lane: string; // StoryJoinLane, kept as `string` on the wire to avoid duplicating the union type
    sim_summary: number | null;
    sim_event: number | null;
    /** Today's bid requests for this URL, from the same signal the trending ranking uses. Null when the signal read failed. */
    bid_requests_today: number | null;
    /** ISO timestamp the URL joined the story. */
    joined_at: string;
}

/** GET /api/clusters/topics/:id — story detail (deepcontext-b3to.7). */
export interface ClustersTopicDetail {
    id: number;
    market: string;
    headline: string | null;
    event: string | null;
    entities: {name: string; type: string}[];
    domains: string[];
    first_seen: string;
    last_seen: string;
    member_count: number;
    domain_count: number;
    members: ClustersStoryMember[];
}

export interface ClustersUrlRow {
    url: string;
    url_hash: string;
    site_domain: string;
    title: string;
    story_id: number;
    join_lane: string;
    sim_event: number | null;
    sim_summary: number | null;
    /** ISO timestamp the member joined its story — used by `sort=recent`. */
    joined_at: string;
}

/** GET /api/clusters/urls — server-side filter + pagination, scoped to visible stories. */
export interface ClustersUrlsResult {
    market: string;
    story_id: number | null;
    q: string | null;
    limit: number;
    offset: number;
    total: number;
    rows: ClustersUrlRow[];
}

export interface StoryFitScore {
    story_id: number;
    headline: string | null;
    event: string | null;
    market: string;
    sim_event: number;
    sim_summary: number;
    fit_score: number;
}

/** POST /api/clusters/score — fit scores per live story for a brief or blueprint. */
export interface ClustersScoringResult {
    market: string;
    brief_length: number;
    embedding_dim: number;
    /** Number of live stories scored. */
    candidate_pool: number;
    scores: StoryFitScore[];
    blueprint_id?: string;
}

/**
 * One topic phrase attached to a trending cluster (bd deepcontext-d68.22).
 */
export interface TrendingClusterTopicPayload {
    phrase: string;
}

export interface TrendingClusterPayload {
    id: string;
    market: string;
    label: string;
    palette_hue: number | null;
    parent_cluster_id: string | null;
    url_count: number;
    impressions: number;
    current_count: number;
    previous_count: number;
    delta_pct: number | null;
    sparkline: number[];
    top_topic_id: string | null;
    topics: TrendingClusterTopicPayload[];
    /** When this story's earliest member was first seen (ISO string; deepcontext-pkyo.3). */
    first_seen: string;
}

/** GET /api/clusters/trending?market=<lang> — velocity-ranked clusters (d68.8). */
export interface TrendingPayload {
    market: string;
    generated_at: string;
    candidate_count: number;
    clusters: TrendingClusterPayload[];
    /**
     * Distinct hourly `cluster_signal_snapshots` points backing this payload,
     * clamped to [0, 48] (deepcontext-ws2b.2). Lets the UI show a warm-up
     * state while the snapshot job is still filling in history.
     */
    snapshot_hours: number;
}

/**
 * One velocity-ranked story on the Trending tab (deepcontext-b3to.6, spec
 * section 10). Replaces `TrendingClusterPayload` as the trending route's read
 * model once the story cutover lands (spec section 13).
 */
export interface TrendingStoryPayload {
    id: number;
    market: string;
    /** 'story' = still live; 'frozen' = past its activity window (spec section 12). */
    status: 'story' | 'frozen';
    headline: string | null;
    event: string | null;
    member_count: number;
    domain_count: number;
    /** Sum of the last 24 hourly deltas of `story_signal_snapshots`. */
    bid_requests_24h: number;
    /**
     * Last 6h of deltas vs the previous 6h. `null` until the story has 12h
     * of snapshot history, or its previous window is 0 — never a division by
     * a zero baseline (spec section 9).
     */
    growth_pct: number | null;
    /** 24 hourly deltas, oldest first. */
    sparkline: number[];
    /**
     * The IAB top-level category shared by most of the story's classified
     * members (deepcontext-ec6z.1). `null` when no member has a
     * ClickHouse `url_classifications` row.
     */
    iab_category: {id: number; label: string} | null;
    /** When this story's earliest member was first seen (ISO string; deepcontext-pkyo.3). */
    first_seen: string;
    /** Stored UMAP topic-similarity map coords (deepcontext-4776.3). `null` until computed. */
    map_x?: number | null;
    map_y?: number | null;
}

/** GET /api/clusters/trending?market=<COUNTRY> — velocity-ranked stories (deepcontext-b3to.6). */
export interface TrendingStoriesPayload {
    market: string;
    generated_at: string;
    candidate_count: number;
    /** Top 20 by 24h volume, then growth (spec section 10). */
    top: TrendingStoryPayload[];
    /** The ranked remainder, same ordering. */
    rest: TrendingStoryPayload[];
    /**
     * Distinct hourly `story_signal_snapshots` points backing this payload,
     * clamped to [0, 48]. Lets the UI show a warm-up state while the
     * snapshot job is still filling in history.
     */
    snapshot_hours: number;
}

/**
 * Top Mentions payload (bd deepcontext-d68.9).
 * Sentiment buckets: 1=Negative, 2=Neutral, 3=Positive.
 */
export interface TopMentionSampleUrl {
    url: string;
    url_hash: string;
    site_domain: string | null;
    sentiment: number | null;
    impressions: number;
}

export interface TopMentionSentimentBreakdown {
    negative: number;
    neutral: number;
    positive: number;
}

export interface TopMentionEntity {
    id: string;
    label: string;
    count: number;
    unique_articles: number;
    total_impressions: number;
    sentiment_breakdown: TopMentionSentimentBreakdown;
    sample_urls: TopMentionSampleUrl[];
}

/** GET /api/clusters/mentions?market&limit — Top Mentions bubbles (d68.9). */
export interface TopMentionsPayload {
    market: string;
    limit: number;
    lookback_days: number;
    sentiment_labels: Record<number, string>;
    entities: TopMentionEntity[];
}
